import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useStore } from './lib/store';
import { api } from './lib/api';
import { wsClient } from './lib/ws';
import { receiveAnswer, receiveIce, endCall, getActive } from './lib/rtc';
import { Login } from './routes/Login';
import { Signup } from './routes/Signup';
import { Verify } from './routes/Verify';
import { Invite } from './routes/Invite';
import { Contacts } from './routes/Contacts';
import { Chat } from './routes/Chat';
import { Call } from './routes/Call';
import { Settings } from './routes/Settings';
import { Admin } from './routes/Admin';
import { OwnerOnboarding } from './routes/OwnerOnboarding';
import { IncomingCallOverlay } from './components/IncomingCallOverlay';

function Protected({ children }: { children: JSX.Element }) {
  const token = useStore((s) => s.accessToken);
  const user = useStore((s) => s.user);
  const loc = useLocation();
  if (!token) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (
    user?.requiresProfileCompletion &&
    loc.pathname !== '/onboarding/owner-details'
  ) {
    return <Navigate to="/onboarding/owner-details" replace />;
  }
  return children;
}

function Bootstrapper({ children }: { children: JSX.Element }) {
  const setAuth = useStore((s) => s.setAuth);
  const clearAuth = useStore((s) => s.clearAuth);
  const token = useStore((s) => s.accessToken);
  const setContacts = useStore((s) => s.setContacts);
  const setContactOnline = useStore((s) => s.setContactOnline);
  const addMessage = useStore((s) => s.addMessage);
  const setIncomingCall = useStore((s) => s.setIncomingCall);

  useEffect(() => {
    // Attempt silent refresh on first load.
    api.refresh().catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) {
      wsClient.close();
      return;
    }
    wsClient.connect(token);
    const off = wsClient.on((msg) => {
      switch (msg.type) {
        case 'hello':
          api.contacts().then(setContacts).catch(() => {});
          break;
        case 'contact.presence':
          setContactOnline(msg.userId, msg.online);
          break;
        case 'chat.new':
        case 'chat.ack':
          addMessage(msg.message);
          break;
        case 'call.incoming': {
          const from = useStore.getState().contacts.find((c) => c.id === msg.from);
          setIncomingCall({
            callId: msg.callId,
            from: msg.from,
            fromName: from?.displayName ?? 'Unknown',
            sdp: msg.sdp,
          });
          break;
        }
        case 'call.answer':
          receiveAnswer(msg.callId, msg.sdp).catch(() => {});
          break;
        case 'call.ice':
          receiveIce(msg.callId, msg.candidate).catch(() => {});
          break;
        case 'call.declined':
        case 'call.ended': {
          const active = getActive(msg.callId);
          if (active) endCall(msg.callId);
          if (useStore.getState().incomingCall?.callId === msg.callId) setIncomingCall(null);
          break;
        }
      }
    });
    return off;
  }, [token, setContacts, setContactOnline, addMessage, setIncomingCall]);

  // ping/pong keepalive
  useEffect(() => {
    if (!token) return;
    const t = setInterval(() => wsClient.send({ type: 'ping' }), 25000);
    return () => clearInterval(t);
  }, [token]);

  // Silent refresh on 401 cascade fail → sign out.
  useEffect(() => {
    function onFocus() {
      if (useStore.getState().accessToken) api.refresh().catch(() => clearAuth());
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [clearAuth, setAuth]);

  return children;
}

export function App() {
  return (
    <BrowserRouter>
      <Bootstrapper>
        <>
          <IncomingCallOverlay />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify/:token" element={<Verify />} />
            <Route path="/invite/:token" element={<Invite />} />
            <Route path="/" element={<Protected><Contacts /></Protected>} />
            <Route path="/chat/:id" element={<Protected><Chat /></Protected>} />
            <Route path="/call/:id" element={<Protected><Call /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/admin" element={<Protected><Admin /></Protected>} />
            <Route path="/onboarding/owner-details" element={<Protected><OwnerOnboarding /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </>
      </Bootstrapper>
    </BrowserRouter>
  );
}
